/**
 * @fileoverview V2 Case Info Step — Incident details, attorney, case manager, CMS integration.
 *
 * Self-contained: owns form state, API hydration, and save logic.
 * Patches the matter on submit. Incident location + summary saved via separate APIs.
 * Not audit-tracked (no section statuses).
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { addMonths, format, parseISO } from 'date-fns';
import { ExternalLink, FolderOpen, Hash, Mail } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { CmsSyncCapabilities } from '@/components/cms-sync/shared';
import { AttorneySelect } from '@/components/matter/AttorneySelect';
import { useListAttorneysQuery } from '@/services/api/attorneysService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useAssignCaseManagerMutation,
  useChangeCaseManagerMutation,
  useGetFirmCaseManagersQuery,
  useRemoveCaseManagerMutation,
} from '@/services/api/caseManagerService';
import {
  useCreateIncidentMutation,
  useGetIncidentsQuery,
  useUpdateIncidentMutation,
} from '@/services/api/incidentService';
import {
  useGetMatterDetailsQuery,
  usePatchMatterMutation,
} from '@/services/api/mattersService';
import { getApiErrorMessage } from '@/utils/api-error';
import { extractAttorneyId } from '@/utils/attorney-helpers';
import {
  getCmsTargetSystemFromUrl,
  getDriveSystemFromUrl,
} from '@/utils/externalSystemUtils';

import {
  FormCheckbox,
  FormError,
  FormField,
  FormGroup,
  FormInput,
  FormLabel,
  FormSection,
  FormTextarea,
  ImportSuggestion,
} from '../components/form';
import { useEntityMatch } from '../hooks/useEntityMatch';
import { useIncidentSummaryNote } from '../hooks/useIncidentSummaryNote';
import { useStepHydration } from '../hooks/useStepHydration';
import { useIngestion } from '../state/IngestionProvider';

// ── Schema ───────────────────────────────────────────────────────────────────

const caseInfoSchema = z.object({
  date_of_loss: z.string().nullable().optional(),
  statute_of_limitations_date: z.string().nullable().optional(),
  incident_location: z.string().optional(),
  incident_summary: z.string().optional(),
  representing_attorney: z.string().nullable().optional(),
  contact_client_directly: z.boolean(),
  case_manager_id: z.string().nullable().optional(),
  auto_task_assignment: z.boolean().nullable().optional(),
  external_cms_url: z
    .string()
    .refine((val) => !val || /^https?:\/\/.+/.test(val), {
      message: 'URL must start with http:// or https://',
    })
    .nullable()
    .optional()
    .or(z.literal('')),
  external_drive_url: z
    .string()
    .refine((val) => !val || /^https?:\/\/.+/.test(val), {
      message: 'URL must start with http:// or https://',
    })
    .nullable()
    .optional()
    .or(z.literal('')),
  cms_email: z.string().email().nullable().optional().or(z.literal('')),
  case_id: z.string().nullable().optional(),
});

type CaseInfoFormValues = z.infer<typeof caseInfoSchema>;

// ── Component ────────────────────────────────────────────────────────────────

export const CaseInfoStep: React.FC = () => {
  const { state, dispatch, registerSave, importData } = useIngestion();
  const matterId = state.matterId!;

  // ── Data fetching ────────────────────────────────────────────────────────
  const { data: matter } = useGetMatterDetailsQuery(matterId ?? skipToken);
  const firmId = matter?.firm?.id ?? '';

  const { data: incidents } = useGetIncidentsQuery(
    matterId ? { matterId } : skipToken,
  );
  const [createIncident] = useCreateIncidentMutation();
  const [updateIncident] = useUpdateIncidentMutation();
  const [incidentId, setIncidentId] = useState<string | null>(null);

  const { existingData: existingIncidentSummary, saveIncidentSummary } =
    useIncidentSummaryNote(matterId);

  const { data: caseManagers = [], isLoading: isLoadingCaseManagers } =
    useGetFirmCaseManagersQuery(
      { firmId: firmId || '' },
      { skip: !firmId },
    );

  const [patchMatter] = usePatchMatterMutation();
  const [assignCaseManager] = useAssignCaseManagerMutation();
  const [changeCaseManager] = useChangeCaseManagerMutation();
  const [removeCaseManager] = useRemoveCaseManagerMutation();

  const existingCaseManagerId = matter?.case_manager?.id ?? null;

  // Attorneys list for fuzzy matching import suggestions
  const { data: attorneys = [] } = useListAttorneysQuery(
    { firm_id: firmId },
    { skip: !firmId },
  );

  const attorneyOptions = useMemo(
    () => attorneys.map((a) => ({
      id: a.id,
      label: `${a.first_name} ${a.last_name}${a.email ? ` (${a.email})` : ''}`.trim(),
    })),
    [attorneys],
  );

  const caseManagerOptions = useMemo(
    () => caseManagers.map((cm) => ({ id: cm.id, label: cm.display_name })),
    [caseManagers],
  );

  // Imported entity names (raw strings for fuzzy matching)
  const importedAttorneyName = importData?.payload?.case_info?.representing_attorney?.presence === 'present'
    ? (importData.payload.case_info.representing_attorney.value as string)
    : null;
  const importedCaseManagerName = importData?.payload?.case_info?.case_manager?.presence === 'present'
    ? (importData.payload.case_info.case_manager.value as string)
    : null;

  // Fuzzy match imported names against entity lists
  const attorneyMatch = useEntityMatch(importedAttorneyName, attorneyOptions);
  const caseManagerMatch = useEntityMatch(importedCaseManagerName, caseManagerOptions);

  // ── Hydration ────────────────────────────────────────────────────────────
  // Track incidentId so save knows whether to create or update
  useEffect(() => {
    if (incidents?.length && !incidentId) {
      setIncidentId(incidents[0].id);
    }
  }, [incidents, incidentId]);

  // Merge all API sources into one form-shaped object
  const saved = useMemo(() => {
    if (!matter) return null;
    return {
      date_of_loss: matter.date_of_loss ?? '',
      statute_of_limitations_date: matter.statute_of_limitations_date ?? '',
      incident_location: incidents?.[0]?.location ?? '',
      incident_summary: existingIncidentSummary ?? '',
      representing_attorney: matter.representing_attorney
        ? typeof matter.representing_attorney === 'string'
          ? matter.representing_attorney
          : matter.representing_attorney.id
        : null,
      contact_client_directly: matter.contact_client_directly ?? true,
      case_manager_id: matter.case_manager?.id ?? null,
      auto_task_assignment: null,
      external_cms_url: matter.external_cms_url ?? '',
      external_drive_url: matter.external_drive_url ?? '',
      cms_email: matter.cms_email ?? '',
      case_id: matter.case_id ?? '',
    };
  }, [matter, incidents, existingIncidentSummary]);

  // Map import payload to ImportedField wrappers in form shape
  const imported = useMemo(() => {
    if (!importData?.payload) return null;
    const ci = importData.payload.case_info;
    return {
      date_of_loss: ci.date_of_loss,
      statute_of_limitations_date: ci.statute_of_limitations_date,
      incident_location: ci.incident_location,
      incident_summary: ci.incident_summary,
      case_id: ci.case_id,
      external_cms_url: ci.external_cms_url,
    };
  }, [importData]);

  // For entity fields, the import has a name but the form stores an ID.
  // assistedOverrides maps the field name to the matched ID so isAssisted
  // can compare the current form value against it.
  const assistedOverrides = useMemo(() => {
    const overrides: Record<string, unknown> = {};
    if (attorneyMatch.bestMatch) overrides.representing_attorney = attorneyMatch.bestMatch.id;
    if (caseManagerMatch.bestMatch) overrides.case_manager_id = caseManagerMatch.bestMatch.id;
    return overrides;
  }, [attorneyMatch.bestMatch, caseManagerMatch.bestMatch]);

  const { form, isAssisted } = useStepHydration<CaseInfoFormValues>({
    schema: caseInfoSchema,
    defaults: {
      date_of_loss: '',
      statute_of_limitations_date: '',
      incident_location: '',
      incident_summary: '',
      representing_attorney: null,
      contact_client_directly: true,
      case_manager_id: null,
      auto_task_assignment: null,
      external_cms_url: '',
      external_drive_url: '',
      cms_email: '',
      case_id: '',
    },
    saved,
    imported,
    assistedOverrides,
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = form;

  // ── Auto-select fuzzy-matched entities ──────────────────────────────────
  const hasAutoSelectedAtty = useRef(false);
  useEffect(() => {
    if (hasAutoSelectedAtty.current || !attorneyMatch.hasMatch) return;
    const currentVal = form.getValues('representing_attorney');
    if (currentVal) return;
    hasAutoSelectedAtty.current = true;
    setValue('representing_attorney', attorneyMatch.bestMatch!.id);
  }, [attorneyMatch, setValue, form]);

  const hasAutoSelectedCm = useRef(false);
  useEffect(() => {
    if (hasAutoSelectedCm.current || !caseManagerMatch.hasMatch) return;
    const currentVal = form.getValues('case_manager_id');
    if (currentVal) return;
    hasAutoSelectedCm.current = true;
    setValue('case_manager_id', caseManagerMatch.bestMatch!.id);
  }, [caseManagerMatch, setValue, form]);

  // ── Watched values ───────────────────────────────────────────────────────
  const dateOfLoss = useWatch({ control, name: 'date_of_loss' });
  const caseManagerId = useWatch({ control, name: 'case_manager_id' });
  const externalCmsUrl = useWatch({ control, name: 'external_cms_url' });
  const externalDriveUrl = useWatch({ control, name: 'external_drive_url' });
  const cmsEmail = useWatch({ control, name: 'cms_email' });

  const hasCaseManager = !!caseManagerId && caseManagerId !== 'none';

  const displayUrl = useMemo(() => {
    try { return new URL(externalCmsUrl ?? '').toString(); } catch { return null; }
  }, [externalCmsUrl]);

  const displayDriveUrl = useMemo(() => {
    try { return new URL(externalDriveUrl ?? '').toString(); } catch { return null; }
  }, [externalDriveUrl]);

  const detectedCms = useMemo(
    () => getCmsTargetSystemFromUrl(externalCmsUrl),
    [externalCmsUrl],
  );
  const detectedDrive = useMemo(
    () => getDriveSystemFromUrl(externalDriveUrl),
    [externalDriveUrl],
  );
  const detectedSystem = detectedCms ?? detectedDrive;

  const driveLabel = detectedDrive === 'DROPBOX'
    ? 'Dropbox Folder URL'
    : detectedDrive === 'GOOGLE_DRIVE'
      ? 'Google Drive Folder URL'
      : 'Drive Folder URL';

  // ── Save ─────────────────────────────────────────────────────────────────
  const saveForm = useCallback(async (data: CaseInfoFormValues): Promise<boolean> => {
    let hasError = false;

    // 1. Patch matter fields
    const patch: Record<string, unknown> = {};
    patch.date_of_loss = data.date_of_loss || null;
    patch.statute_of_limitations_date = data.statute_of_limitations_date || null;
    if (data.contact_client_directly !== undefined)
      patch.contact_client_directly = data.contact_client_directly;
    if (data.representing_attorney)
      patch.representing_attorney = extractAttorneyId(data.representing_attorney);
    if (data.external_cms_url !== undefined)
      patch.external_cms_url = data.external_cms_url || null;
    if (data.external_drive_url !== undefined)
      patch.external_drive_url = data.external_drive_url || null;
    if (data.cms_email !== undefined) patch.cms_email = data.cms_email || null;
    if (data.case_id !== undefined) patch.case_id = data.case_id || null;

    if (Object.keys(patch).length > 0) {
      try {
        await patchMatter({ matter_id: matterId, patch }).unwrap();
      } catch (error) {
        hasError = true;
        toast.error(getApiErrorMessage(error, 'Some case info could not be saved.'));
      }
    }

    // 2. Case manager changes
    const nextCmId = data.case_manager_id ?? null;
    if (nextCmId !== existingCaseManagerId) {
      try {
        if (!nextCmId && existingCaseManagerId) {
          await removeCaseManager({ matterId, data: { revert_tasks_to_mp: true } }).unwrap();
        } else if (nextCmId && !existingCaseManagerId) {
          await assignCaseManager({
            matterId,
            data: { case_manager_id: nextCmId, auto_task_assignment: data.auto_task_assignment ?? true },
          }).unwrap();
        } else if (nextCmId && existingCaseManagerId) {
          await changeCaseManager({
            matterId,
            data: { new_case_manager_id: nextCmId, auto_task_assignment: data.auto_task_assignment ?? true },
          }).unwrap();
        }
      } catch (error) {
        hasError = true;
        toast.error(getApiErrorMessage(error, 'Failed to update case manager.'));
      }
    }

    // 3. Incident location
    const loc = data.incident_location?.trim();
    if (loc !== undefined) {
      try {
        if (incidentId) {
          await updateIncident({ matterId, incidentId, incident: { location: loc || null } }).unwrap();
        } else {
          const newIncident = await createIncident({ matterId, incident: { location: loc || null } }).unwrap();
          setIncidentId(newIncident.id);
        }
      } catch (error) {
        hasError = true;
        toast.error(getApiErrorMessage(error, 'Incident location could not be saved.'));
      }
    }

    // 4. Incident summary
    const summary = data.incident_summary?.trim();
    if (summary !== undefined) {
      try {
        await saveIncidentSummary(summary || '');
      } catch (error) {
        hasError = true;
        toast.error(getApiErrorMessage(error, 'Incident summary could not be saved.'));
      }
    }

    if (!hasError) {
      toast.success('Case info saved');
    }

    return !hasError;
  }, [matterId, patchMatter, existingCaseManagerId, assignCaseManager, changeCaseManager, removeCaseManager, incidentId, createIncident, updateIncident, saveIncidentSummary]);

  // Register save callback so sidebar navigation triggers save
  useEffect(() => {
    registerSave(async () => {
      let result = false;
      await handleSubmit(
        async (data) => { result = await saveForm(data); },
        () => { result = false; }, // validation failed
      )();
      return result;
    });
    return () => registerSave(null);
  }, [registerSave, handleSubmit, saveForm]);

  const onSubmit = async (data: CaseInfoFormValues) => {
    const success = await saveForm(data);
    if (success) {
      dispatch({ type: 'GO_TO_STEP', index: state.stepIndex + 1 });
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {/* Incident Details */}
      <FormSection title="Incident Details">
        <FormGroup>
          <div className="grid grid-cols-2 gap-4">
            <FormField assisted={isAssisted('date_of_loss')}>
              <FormLabel htmlFor="v2-dol">Date of Loss</FormLabel>
              <Controller
                name="date_of_loss"
                control={control}
                render={({ field }) => (
                  <FormInput
                    id="v2-dol"
                    type="date"
                    disabled={isSubmitting}
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
            </FormField>

            <FormField assisted={isAssisted('statute_of_limitations_date')}>
              <FormLabel htmlFor="v2-sol">Statute of Limitations</FormLabel>
              <Controller
                name="statute_of_limitations_date"
                control={control}
                render={({ field }) => (
                  <FormInput
                    id="v2-sol"
                    type="date"
                    disabled={isSubmitting}
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
              <div className="flex items-center gap-2 mt-1">
                {[12, 18, 24].map((months) => (
                  <button
                    key={months}
                    type="button"
                    disabled={!dateOfLoss || isSubmitting}
                    className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline disabled:text-zinc-400 disabled:no-underline disabled:cursor-not-allowed"
                    onClick={() => {
                      if (!dateOfLoss) return;
                      const sol = addMonths(parseISO(dateOfLoss), months);
                      setValue('statute_of_limitations_date', format(sol, 'yyyy-MM-dd'));
                    }}
                  >
                    +{months}mo
                  </button>
                ))}
                {!dateOfLoss && (
                  <span className="text-xs text-zinc-400">Enter DOL first</span>
                )}
              </div>
            </FormField>
          </div>

          <FormField assisted={isAssisted('incident_location')}>
            <FormLabel htmlFor="v2-location">Incident Location</FormLabel>
            <Controller
              name="incident_location"
              control={control}
              render={({ field }) => (
                <FormTextarea
                  id="v2-location"
                  rows={2}
                  disabled={isSubmitting}
                  placeholder="e.g., 123 Main St, Los Angeles, CA"
                  {...field}
                  value={field.value ?? ''}
                />
              )}
            />
          </FormField>

          <FormField assisted={isAssisted('incident_summary')}>
            <FormLabel htmlFor="v2-summary">Incident Summary</FormLabel>
            <Controller
              name="incident_summary"
              control={control}
              render={({ field }) => (
                <FormTextarea
                  id="v2-summary"
                  rows={4}
                  disabled={isSubmitting}
                  placeholder="Brief description of the accident/incident"
                  {...field}
                  value={field.value ?? ''}
                />
              )}
            />
          </FormField>
        </FormGroup>
      </FormSection>

      {/* Contact & Team */}
      <FormSection title="Contact & Team">
        <FormGroup>
          <div className="grid grid-cols-2 gap-4">
            <FormField assisted={isAssisted('representing_attorney')}>
              <FormLabel>Attorney</FormLabel>
              <Controller
                name="representing_attorney"
                control={control}
                render={({ field }) => (
                  <AttorneySelect
                    firmId={firmId}
                    value={field.value ?? null}
                    onChange={(val) => field.onChange(val)}
                    placeholder="Select attorney"
                  >
                    <ImportSuggestion
                      importedName={attorneyMatch.importedName}
                      matched={attorneyMatch.hasMatch}
                      currentValueId={field.value}
                    />
                  </AttorneySelect>
                )}
              />
            </FormField>

            <FormField>
              <FormLabel>Contact Client Directly</FormLabel>
              <Controller
                name="contact_client_directly"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value ? 'yes' : 'no'}
                    onValueChange={(v) => onChange(v === 'yes')}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes - Finch may contact client</SelectItem>
                      <SelectItem value="no">No - Only firm contacts client</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </FormField>
          </div>
          {/* Case Manager */}
          {firmId && caseManagers.length > 0 && (
            <>
              <FormField assisted={isAssisted('case_manager_id')}>
                <FormLabel>Case Manager</FormLabel>
                <Controller
                  name="case_manager_id"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Select
                        value={field.value || 'none'}
                        onValueChange={(v) => field.onChange(v === 'none' ? null : v)}
                        disabled={isSubmitting || isLoadingCaseManagers}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="None (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            <span className="text-zinc-400">None</span>
                          </SelectItem>
                          {caseManagers.map((cm) => (
                            <SelectItem key={cm.id} value={cm.id}>
                              {cm.display_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <ImportSuggestion
                        importedName={caseManagerMatch.importedName}
                        matched={caseManagerMatch.hasMatch}
                        currentValueId={field.value}
                      />
                    </>
                  )}
                />
              </FormField>
              {hasCaseManager && (
                <Controller
                  name="auto_task_assignment"
                  control={control}
                  render={({ field }) => (
                    <FormCheckbox
                      label="Auto-assign tasks"
                      description="Tasks go to the case manager"
                      checked={field.value ?? true}
                      onChange={(e) => field.onChange((e.target as HTMLInputElement).checked)}
                      disabled={isSubmitting}
                    />
                  )}
                />
              )}
            </>
          )}
        </FormGroup>
      </FormSection>

      {/* CMS Integration */}
      <FormSection title="CMS Integration">
        <FormGroup>
          <div className="grid grid-cols-2 gap-4">
            <FormField hasError={!!errors.external_cms_url} assisted={isAssisted('external_cms_url')}>
              <FormLabel>External CMS URL</FormLabel>
              <Controller
                name="external_cms_url"
                control={control}
                render={({ field }) => (
                  <div className="flex gap-2">
                    <FormInput
                      placeholder="https://..."
                      invalid={!!errors.external_cms_url}
                      disabled={isSubmitting}
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                    {displayUrl && !errors.external_cms_url && (
                      <button
                        type="button"
                        onClick={() => window.open(displayUrl, '_blank')}
                        className="shrink-0 rounded-lg border border-zinc-200 p-2 text-zinc-500 hover:text-zinc-700 hover:border-zinc-300"
                        title="Open in CMS"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              />
              <FormError>{errors.external_cms_url?.message}</FormError>
            </FormField>

            <FormField hasError={!!errors.external_drive_url}>
              <FormLabel>{driveLabel}</FormLabel>
              <Controller
                name="external_drive_url"
                control={control}
                render={({ field }) => (
                  <div className="flex gap-2">
                    <FormInput
                      placeholder="https://drive.google.com/... or https://dropbox.com/..."
                      invalid={!!errors.external_drive_url}
                      disabled={isSubmitting}
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                    {displayDriveUrl && !errors.external_drive_url && (
                      <button
                        type="button"
                        onClick={() => window.open(displayDriveUrl, '_blank')}
                        className="shrink-0 rounded-lg border border-zinc-200 p-2 text-zinc-500 hover:text-zinc-700 hover:border-zinc-300"
                        title="Open drive folder"
                      >
                        <FolderOpen className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              />
              <FormError>{errors.external_drive_url?.message}</FormError>
            </FormField>

            <FormField hasError={!!errors.cms_email}>
              <FormLabel>Matter Email</FormLabel>
              <Controller
                name="cms_email"
                control={control}
                render={({ field }) => (
                  <div className="flex gap-2">
                    <FormInput
                      type="email"
                      placeholder="e.g., matter-12345@firm.filevine.io"
                      invalid={!!errors.cms_email}
                      disabled={isSubmitting}
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                    {cmsEmail && !errors.cms_email && (
                      <button
                        type="button"
                        onClick={() => window.open(`mailto:${cmsEmail}`, '_blank')}
                        className="shrink-0 rounded-lg border border-zinc-200 p-2 text-zinc-500 hover:text-zinc-700 hover:border-zinc-300"
                        title="Compose email"
                      >
                        <Mail className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              />
              <FormError>{errors.cms_email?.message}</FormError>
            </FormField>

            <FormField assisted={isAssisted('case_id')}>
              <FormLabel>Case ID</FormLabel>
              <Controller
                name="case_id"
                control={control}
                render={({ field }) => (
                  <div className="flex gap-2">
                    <FormInput
                      placeholder="e.g., 2737337"
                      disabled={isSubmitting}
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                    {field.value && (
                      <span className="shrink-0 flex items-center rounded-lg border border-zinc-200 p-2 text-zinc-400">
                        <Hash className="h-4 w-4" />
                      </span>
                    )}
                  </div>
                )}
              />
            </FormField>
          </div>

          <CmsSyncCapabilities detectedCms={detectedSystem} defaultOpen={false} />
        </FormGroup>
      </FormSection>

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting}
        className={
          'inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold ' +
          'bg-blue-600 text-white shadow-sm hover:bg-blue-700 ' +
          'disabled:opacity-50 disabled:cursor-not-allowed ' +
          'transition-colors duration-150'
        }
      >
        {isSubmitting ? 'Saving…' : 'Save & Continue'}
      </button>
    </form>
  );
};
